import { useFormik } from "formik";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import ReactQuill from "react-quill";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { KTIcon } from "../../../../_metronic/helpers";
import { Pagination } from "../../../components/common/pagination/Pagination";
import { MyTooltip } from "../../../components/common/tooltip/Tooltip";
import { EmptySection } from "../../../components/sections/empty/Empty";
import { useDeleteModal } from "../../../context/DeleteModalContext";
import { useNotification } from "../../../context/NotificationContext";
import { useDebounce } from "../../../hooks/useDebounce";
import { LANGUAGES } from "../../../mock/languages";
import { quillConfig } from "../../../mock/quill-config";
import { MODERATOR_QUESTIONARE } from "../../../mock/URLs";
import {
  getVoteQuestionCategoryById,
  getVoteQuestionsByCategoryId,
  updateVoteQuestion,
  updateVoteQuestionItem,
} from "../../../services/voteQuestionSvc";
import { lang } from "../../../types/lang";

export const VoteCategoryUpdatePage = () => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { showNotification } = useNotification();
  const { categoryId } = useParams();
  const [activeTab, setActiveTab] = useState<lang>("uz");
  const { data: categoryData } = useQuery("", () =>
    getVoteQuestionCategoryById(Number(categoryId))
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: {
        uz: categoryData?.data?.title_uz || "",
        ru: categoryData?.data?.title_ru || "",
        en: categoryData?.data?.title_en || "",
      },
      description: {
        uz: categoryData?.data?.description_uz || "",
        ru: categoryData?.data?.description_ru || "",
        en: categoryData?.data?.description_en || "",
      },
      is_active: categoryData?.data?.is_active || false,
    },
    onSubmit: async (values, { resetForm }) => {
      const formData = new FormData();
      formData.append("title_uz", values.title.uz);
      formData.append("title_ru", values.title.ru);
      formData.append("title_en", values.title.en);
      formData.append("description_uz", values.description.uz);
      formData.append("description_ru", values.description.ru);
      formData.append("description_en", values.description.en);
      formData.append("is_active", values.is_active.toString());

      try {
        const res = await updateVoteQuestion(categoryId!, formData);
        if (res?.success) {
          showNotification(
            "Muvaffaqiyatli",
            "So'rovnoma yangilandi",
            "success"
          );
          setTimeout(() => {
            navigate(-1);
          }, 1000);
        }
      } catch (e) {
        showNotification("Xatolik!", "", "error");
      }
    },
  });

  const handleTabChange = (lang) => {
    setActiveTab(lang);
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") || "1", 10);
  const searchQuery = searchParams.get("search") || "";

  const debouncedSearch = useDebounce(searchQuery, 500);

  const { data: questionsByCategoryIdData, refetch: refetchCategory } =
    useQuery(["getVoteQuestionsByCategoryId", page, debouncedSearch], () =>
      getVoteQuestionsByCategoryId({
        params: {
          category: categoryId!,
          page,
          search: debouncedSearch,
        },
      })
    );

  return (
    <div>
      <div className={"card"}>
        <div className="card-header">
          <h1 className="card-title">So'rovnomani yangilash</h1>
          <ul className="nav nav-tabs nav-line-tabs nav-stretch fs-6 border-0">
            {LANGUAGES.map((lang) => (
              <li className="nav-item" key={lang.key}>
                <button
                  className={`nav-link ${
                    activeTab === lang.key ? "active" : ""
                  }`}
                  onClick={() => handleTabChange(lang.key)}
                >
                  {lang.name}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="card-body m-0 p-0">
          <div id="kt_account_profile_details" className="collapse show">
            <form
              className="form"
              onSubmit={formik.handleSubmit}
              encType="multipart/form-data"
            >
              <div className="card-body p-9">
                <div className="row mb-6">
                  <label className="col-lg-4 col-form-label required fw-bold fs-6">
                    So'rovnoma nomi
                  </label>
                  <div className="col-lg-8 fv-row">
                    <textarea
                      className="form-control form-control-lg form-control-solid"
                      placeholder="So'rovnoma nomi"
                      name={`title.${activeTab}`}
                      value={formik.values.title[activeTab]}
                      onChange={formik.handleChange}
                    />
                    {formik.touched.title?.[activeTab] &&
                      formik.errors.title?.[activeTab] && (
                        <div className="text-danger">
                          {formik.errors.title[activeTab]?.toString()}
                        </div>
                      )}
                  </div>
                </div>

                <div className="row mb-6">
                  <label className="col-lg-4 col-form-label fw-bold fs-6">
                    <span className="required">So'rovnoma matni</span>
                  </label>
                  <div className="col-lg-8 fv-row">
                    <div className={"h-200px"}>
                      <ReactQuill
                        key={activeTab}
                        style={{ height: "150px" }}
                        formats={quillConfig?.formats}
                        modules={quillConfig?.modules}
                        value={formik.values.description[activeTab]}
                        onChange={(value) =>
                          formik.setFieldValue(
                            `description.${activeTab}`,
                            value
                          )
                        }
                      />
                    </div>
                    {formik.touched.description?.[activeTab] &&
                      formik.errors.description?.[activeTab] && (
                        <div className="text-danger">
                          {formik.errors.description[activeTab]?.toString()}
                        </div>
                      )}
                  </div>
                </div>

                <div className="row mb-6">
                  <label className="col-lg-4 col-form-label fw-bold fs-6">
                    Holatni aktivlashtirish
                  </label>
                  <div className="col-lg-8 fv-row">
                    <div className="form-check form-check-solid form-switch fv-row">
                      <input
                        className="form-check-input w-45px h-30px"
                        type="checkbox"
                        name={"is_active"}
                        checked={formik.values.is_active}
                        onChange={(e) =>
                          formik.setFieldValue("is_active", e.target.checked)
                        }
                      />
                      <label className="form-check-label"></label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-footer">
                <div className="justify-content-end d-flex gap-2">
                  <button
                    className="btn btn-secondary"
                    onClick={() => navigate(-1)}
                    type="button"
                  >
                    {formatMessage({ id: "Bekor qilish" })}
                  </button>
                  <button
                    className="btn btn-primary"
                    type="submit"
                    onClick={() => formik.handleSubmit()}
                    disabled={!formik.isValid || formik.isSubmitting}
                  >
                    {formik.isSubmitting ? "Saqlanmoqda" : "Saqlash"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <VoteQuestionsSection
        questionsByCategoryIdData={questionsByCategoryIdData}
        refetchCategory={refetchCategory}
      />
    </div>
  );
};

const VoteQuestionsSection = ({
  questionsByCategoryIdData,
  refetchCategory,
}) => {
  const totalItems = questionsByCategoryIdData?.total_items || 0;
  const itemsPerPage = questionsByCategoryIdData?.page_size || 8;
  const { categoryId } = useParams();

  const handleStatusChange = async (id: number, currentStatus: boolean) => {
    try {
      await updateVoteQuestionItem(id, { is_active: !currentStatus });
      await refetchCategory();
    } catch (error) {
      console.error("Failed to update status", error);
    }
  };

  const { openDeleteModal } = useDeleteModal();

  const handleDelete = (id: number) => {
    openDeleteModal(MODERATOR_QUESTIONARE, id, refetchCategory);
  };

  return (
    <div className="card my-10">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="card-title">So'rovnoma savollari</h5>
        <Link
          className="btn btn-primary btn-sm"
          to={`/vote-category/update/${categoryId}/question/create`}
        >
          <KTIcon iconName="plus-square" />
          Qo'shish
        </Link>
      </div>
      <div className="card-body">
        {questionsByCategoryIdData?.data.length > 0 ? (
          <>
            {questionsByCategoryIdData?.data.map((question) => (
              <div className={"bg-gray-100 p-5 my-2 rounded"}>
                <div className="d-flex justify-content-between align-items-start">
                  <div className="d-flex justify-content-between align-items-start w-100">
                    <div>
                      <h4>{question.question}</h4>
                      <ul>
                        {question.answers.map((answer) => (
                          <li className={answer.is_active ?? "text-muted"}>
                            {answer.answer}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <MyTooltip title={"Holatni aktivlashtirish"} position="top">
                      <div className="form-check form-check-solid form-switch fv-row">
                        <input
                          className="form-check-input w-30px h-20px"
                          type="checkbox"
                          name={"is_active"}
                          checked={question.is_active}
                          onChange={() =>
                            handleStatusChange(question.id, question.is_active)
                          }
                        />
                        <label className="form-check-label"></label>
                      </div>
                    </MyTooltip>
                  </div>

                  <div className="d-flex flex-column justify-content-end gap-2">
                    <button
                      className="btn btn-danger btn-sm btn-light"
                      onClick={() => handleDelete(question.id)}
                    >
                      O'chirish
                    </button>
                  </div>
                </div>
                <span className="badge badge-primary">
                  {question.category.title}
                </span>
              </div>
            ))}
            <div className="mt-10">
              <Pagination totalItems={totalItems} itemsPerPage={itemsPerPage} />
            </div>
          </>
        ) : (
          <EmptySection height={"20vh"} />
        )}
      </div>
    </div>
  );
};
